import React from 'react';

import { StatCard } from './StatCard';
import { CONSULTATION_IDS, METRIC_LENS, type MetricId, activityIds } from './metricLens';
import { useRovingRadioGroup } from './useRovingRadioGroup';

// The 8-card Account Overview radiogroup (ENG-5256). A single role="radiogroup"
// holds two role="group" sub-groups in the product-confirmed order:
//   Activity (4):      [money slot] · Projects Created · Expert Requests Created · Experts Engaged
//   Consultations (4): Completed · Reviewed · Pending Reviews · Avg Rating
// The money slot is polymorphic: "Credits Spent" (credit) or "Amount Spent"
// (SMB). Avg Rating is the 4th Consultations card, NOT its own section. Roving
// focus is linear across ALL 8 cards (wrap + Home/End) via useRovingRadioGroup,
// so the sub-group boundary is purely a labelling/visual division. Default =
// Completed.

/** Per-card display data computed by the orchestrator from live stats + lens. */
export interface StatCardView {
  /** Pre-formatted value; null (Avg Rating with no ratings) renders "—". */
  value: string | null;
  /** Glyph shown to the right of the value (Avg Rating's ★); omitted otherwise. */
  valueSuffix?: string;
  /** Lens line for the chart heading (e.g. "42 completed · monthly"); not shown on the card. */
  subtitle: string;
}

interface StatCardRadioGroupProps {
  /** Display data keyed by metric id. */
  cards: Record<MetricId, StatCardView>;
  /** All 8 ids in card/roving order; money slot reflects the dashboard type. */
  orderedIds: MetricId[];
  /** The id occupying the first Activity slot (credits-spent or amount-spent). */
  moneyMetric: MetricId;
  selectedId: MetricId;
  onSelect: (id: MetricId) => void;
}

export function StatCardRadioGroup({
  cards,
  orderedIds,
  moneyMetric,
  selectedId,
  onSelect,
}: StatCardRadioGroupProps): React.ReactNode {
  const subGroups: { label: string; ids: MetricId[] }[] = [
    { label: 'Activity', ids: activityIds(moneyMetric) },
    { label: 'Consultations', ids: CONSULTATION_IDS },
  ];

  const { getOptionProps } = useRovingRadioGroup({
    ids: orderedIds,
    selectedId,
    onSelect: (id) => onSelect(id as MetricId),
  });

  return (
    <div role="radiogroup" aria-label="Account metric" className="flex flex-col gap-12 sm:flex-row">
      {subGroups.map((subGroup) => {
        const labelId = `account-metric-group-${subGroup.label.toLowerCase()}`;
        return (
          <div key={subGroup.label} className="flex min-w-0 flex-1 flex-col gap-6">
            <h4
              id={labelId}
              className="text-10 font-semibold uppercase tracking-wider text-on-surface-muted"
            >
              {subGroup.label}
            </h4>
            <div
              role="group"
              aria-labelledby={labelId}
              className="flex min-w-0 flex-1 items-stretch gap-8"
            >
              {subGroup.ids.map((id) => (
                <div key={id} className="flex min-w-0 flex-1 items-stretch">
                  <StatCard
                    label={METRIC_LENS[id].label}
                    value={cards[id].value}
                    valueSuffix={cards[id].valueSuffix}
                    selected={id === selectedId}
                    optionProps={getOptionProps(id)}
                  />
                </div>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}
